// src/pages/dashboard/DashboardPage.tsx
import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import "../assets/css/dashboard.css";


import { DashboardDisposition, useDashboard } from "../hooks/dashboard/useDashboard";
import DashboardToolbar from "../components/Dashboard2/toolbar/DashboardToolbar";
import DashboardGrid from "../components/Dashboard2/grid/DashboardGrid";
import AddWidgetModal from "../components/Dashboard2/modal/AddWidgetModal";
import ConfirmDeleteModal from "../components/Dashboard2/modal/ConfirmDeleteModal";
import WidgetSettingsDrawer from "../components/Dashboard2/modal/WidgetSettingsDrawer";
import CraftDashboardBuilder, { CRAFT_DASHBOARD_SAVE_EVENT, CraftDashboardView, hasCraftDashboardState } from "../components/Dashboard2/CraftDashboardBuilder";

import type { DashboardWidget } from "../types/dashboard/dashboard.types";
import { useTranslate } from "../hooks/LanguageProvider";

export default function DashboardPage() {
    const id_dashboard = 2;
    const [showModal, setShowModal] = useState(false);
    const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
    const [widgetToDelete, setWidgetToDelete] = useState<DashboardWidget | null>(null);
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [draftSections, setDraftSections] = useState<Array<{ id: string; pattern: number[] }>>([]);
    const [pendingLayout, setPendingLayout] = useState<(Pick<DashboardWidget, "x" | "y" | "w" | "h"> & { draftSectionId?: string }) | null>(null);

    // ✅ Edit mode ON/OFF
    const [isEditMode, setIsEditMode] = useState(false);

    const { translate } = useTranslate();

    const {
        widgets,
        layouts,
        loading,
        saveStatus,
        canSave,
        setCurrentBreakpoint,
        onLayoutChange,
        updateWidgetLayout,
        updateWidgetContent,
        moveWidget,
        addNewWidget,
        removeWidget,
        save,
    } = useDashboard(id_dashboard);

    const dispositionPatterns: Record<DashboardDisposition, number[]> = {
        auto: [4, 4, 4],
        one: [12],
        two: [6, 6],
        three: [4, 4, 4],
        four: [3, 3, 3, 3],
        six: [2, 2, 2, 2, 2, 2],
        "70-30": [8, 4],
        "30-70": [4, 8],
        "25-50-25": [3, 6, 3],
        "20-60-20": [2, 8, 2],
    };

    const addDraftSection = (disposition: DashboardDisposition) => {
        const pattern = dispositionPatterns[disposition] ?? dispositionPatterns.two;
        setDraftSections((prev) => [...prev, { id: `draft-${Date.now()}-${prev.length}`, pattern }]);
    };

    return (
        <Container fluid className="mt-4">

            <button
                type="button"
                className={`edit-float-btn ${isEditMode ? "active" : ""}`}
                onClick={async () => {
                    if (isEditMode) {
                        window.dispatchEvent(new Event(CRAFT_DASHBOARD_SAVE_EVENT));
                    } else if (canSave) {
                        await save();
                    }
                    setIsEditMode((v) => !v);
                }}
                title={isEditMode ? translate("Mode affichage") : translate("Edit")}
            >
                <i className={`ri-${isEditMode ? "check-line" : "edit-line"}`}></i>
                <span className="label">
                    {isEditMode ? translate("Mode affichage") : translate("Edit")}
                </span>
            </button>

            {isEditMode ? (
                <CraftDashboardBuilder />
            ) : (
                <>
            {false && (
                <Row className="align-items-center mb-3">
                    <Col xs={12} md={6}>
                        <h4 className="mb-2 mb-md-0 d-flex align-items-center gap-2">
                            <i className="fas fa-edit text-primary"></i>
                            {translate("Edit dashboard")}
                        </h4>
                    </Col>

                    <Col xs={12}>
                        <DashboardToolbar
                            onAdd={() => setShowModal(true)}
                            onSave={save}
                            saveStatus={saveStatus}
                            canSave={canSave}
                            selectedWidget={widgets.find((widget) => widget.id === selectedWidgetId) ?? null}
                            onDispositionChange={addDraftSection}
                            onOpenSettings={() => setShowSettingsDrawer(true)}
                        />
                    </Col>
                </Row>
            )}

            {/* Grid */}
            {hasCraftDashboardState() ? (
                <CraftDashboardView />
            ) : loading ? (
                <div>Chargement...</div>
            ) : (
                <DashboardGrid
                    widgets={widgets}
                    layouts={layouts}
                    isEditMode={isEditMode}
                    onLayoutChange={onLayoutChange}
                    onBreakpointChange={(bp: any) => setCurrentBreakpoint(bp as any)}
                    onRequestDelete={(widget) => setWidgetToDelete(widget)}
                    selectedWidgetId={selectedWidgetId}
                    onSelectWidget={(widget) => setSelectedWidgetId(widget.id)}
                    draftSections={draftSections}
                    onRequestAdd={() => {
                        setPendingLayout(null);
                        setShowModal(true);
                    }}
                    onRequestAddToColumn={(layout) => {
                        setPendingLayout(layout);
                        setShowModal(true);
                    }}
                    onRequestEdit={(widget) => {
                        setSelectedWidgetId(widget.id);
                        setShowSettingsDrawer(true);
                    }}
                    onDuplicateWidget={async (widget) => {
                        await addNewWidget(widget.content, widget.type);
                    }}
                    onMoveWidget={moveWidget}
                />
            )}
                </>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmDeleteModal
                show={!!widgetToDelete}
                onCancel={() => setWidgetToDelete(null)}
                onConfirm={async () => {
                    if (!widgetToDelete) return;
                    await removeWidget(widgetToDelete.id_widget);
                    if (selectedWidgetId === widgetToDelete.id) setSelectedWidgetId(null);
                    setWidgetToDelete(null);
                }}
                message={`${translate("Do you really want to delete")} "${widgetToDelete?.type}" ?`}
            />

            {/* Add Widget Modal */}
            <AddWidgetModal
                show={showModal}
                handleClose={() => {
                    setShowModal(false);
                    setPendingLayout(null);
                }}
                onApplyDisposition={addDraftSection}
                handleAddWidget={async (content: string, type: string) => {
                    await addNewWidget(content, type, pendingLayout ?? undefined);
                    if (pendingLayout?.draftSectionId) {
                        setDraftSections((prev) => prev.filter((section) => section.id !== pendingLayout.draftSectionId));
                    }
                    setPendingLayout(null);
                    setShowModal(false);
                }}
            />

            <WidgetSettingsDrawer
                show={showSettingsDrawer}
                widget={widgets.find((widget) => widget.id === selectedWidgetId) ?? null}
                onClose={() => setShowSettingsDrawer(false)}
                onSave={(widgetId, content) => updateWidgetContent(widgetId, { content })}
                onLayoutChange={updateWidgetLayout}
            />
        </Container>
    );
}
